import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Mail, Lock, User, Building2, Phone, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";

const LogoIcon = () => (
  <Link to="/" className="flex items-center gap-3">
    <div className="w-10 h-10 bg-gradient-to-br from-[#3b82f6] to-[#06b6d4] flex items-center justify-center">
      <span className="text-white font-black text-lg">G</span>
    </div>
    <div>
      <span className="text-white font-bold text-sm tracking-wide">GLOBAL <span className="text-[#3b82f6]">METAFORA</span></span>
      <p className="text-[#94a3b8] text-[9px] tracking-wider">PORTAL DE CLIENTES</p>
    </div>
  </Link>
);

function formatError(detail) {
  if (detail == null) return "Algo salio mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", phone: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData);
      }
      navigate("/portal");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#02040a] flex items-center justify-center px-4 py-8" data-testid="auth-page">
      <div className="noise-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <LogoIcon />
          </div>
          <h1 className="text-white text-2xl font-bold mb-2" data-testid="auth-title">
            {isLogin ? "Iniciar Sesion" : "Crear Cuenta"}
          </h1>
          <p className="text-[#94a3b8] text-sm">
            {isLogin ? "Accede a tu portal de cliente" : "Registrate para acceder a demos y soporte"}
          </p>
        </div>

        <div className="bg-[#0b1221] border border-white/10 p-6 sm:p-8">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
              data-testid="auth-error"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    placeholder="Tu nombre"
                    className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                    data-testid="auth-input-name"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[#94a3b8] text-xs mb-1.5">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="tu@email.com"
                  className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                  data-testid="auth-input-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#94a3b8] text-xs mb-1.5">Contrasena *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Min. 6 caracteres"
                  className="w-full bg-[#02040a] border border-white/10 pl-10 pr-10 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                  data-testid="auth-input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Empresa</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                      <input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Tu empresa"
                        className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                        data-testid="auth-input-company"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Telefono</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                      <input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 555-0000"
                        className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                        data-testid="auth-input-phone"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3b82f6] text-white font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              whileHover={!loading ? { boxShadow: "0 0 30px rgba(59,130,246,0.4)" } : {}}
              whileTap={{ scale: 0.98 }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {isLogin ? "Iniciar Sesion" : "Crear Cuenta"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="text-[#94a3b8] text-sm hover:text-[#3b82f6] transition-colors"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? (
                <>No tienes cuenta? <span className="text-[#3b82f6] font-medium">Registrate</span></>
              ) : (
                <>Ya tienes cuenta? <span className="text-[#3b82f6] font-medium">Inicia Sesion</span></>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-[#94a3b8]/40 text-xs mt-6">
          Global Metafora APP - Portal de Clientes
        </p>
      </motion.div>
    </div>
  );
};

export default AuthPage;
